import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { NAV, LINKEDIN_URL } from "../../data/site";
import { scrollToSection, LANDING_PATHS } from "../../lib/scroll";
import { ArrowRight, Close, Menu, LinkedIn } from "../ui/Icons";
import "./Header.css";

const Header = ({ setScrollingManually }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const onLanding = LANDING_PATHS.includes(location.pathname);
  const active = location.pathname === "/" ? "home" : location.pathname.slice(1);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const go = (id) => {
    setOpen(false);
    const path = id === "home" ? "/" : `/${id}`;
    if (onLanding) {
      setScrollingManually(true);
      navigate(path, { state: { fromHeaderClick: true } });
      scrollToSection(id);
      setTimeout(() => setScrollingManually(false), 900);
    } else {
      navigate(path);
    }
  };

  return (
    <>
      <header className={`hdr ${scrolled ? "hdr--scrolled" : ""}`}>
        <div className="hdr__bar">
          <button
            type="button"
            className="hdr__brand"
            onClick={() => go("home")}
            aria-label="Episcope AI, back to top"
          >
            <img className="hdr__logo" src="/logo.png" alt="" width="30" height="30" />
            <span className="hdr__word">
              Episcope<em>AI</em>
            </span>
          </button>

          <nav className="hdr__nav" aria-label="Primary">
            {NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`hdr__link ${active === item.id ? "is-active" : ""}`}
                onClick={() => go(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hdr__cta">
            <button
              type="button"
              className="btn btn-primary btn-sm hdr__demo"
              onClick={() => go("contact")}
            >
              Book a demo <ArrowRight className="i" />
            </button>
            <button
              type="button"
              className="hdr__burger"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <Close /> : <Menu />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="mmenu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <ul className="mmenu__list">
              {[{ id: "home", label: "Home" }, ...NAV].map((item, i) => (
                <motion.li
                  key={item.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
                >
                  <button
                    type="button"
                    className={`mmenu__link ${active === item.id ? "is-active" : ""}`}
                    onClick={() => go(item.id)}
                  >
                    <span className="mmenu__idx mono">0{i + 1}</span>
                    {item.label}
                  </button>
                </motion.li>
              ))}
            </ul>
            <div className="mmenu__foot">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => go("contact")}
              >
                Book a demo <ArrowRight className="i" />
              </button>
              <a
                className="mmenu__social"
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Episcope AI on LinkedIn"
              >
                <LinkedIn />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
